// Form-side overrides on top of `@barel/core/schemas/user.ts`.
//
// Per the orchestrator's plan-corpus follow-up flags:
//   1. Core's `signUpSchema` uses `password.min(10)` while `auth.md` says
//      `min(8)` — we follow core (10).
//   2. Core's `signUpSchema.name` is `.optional()` while US-001 requires it
//      from the form. We override `name` to required at form level; core
//      stays permissive for API flexibility.
//
// We also tighten the reset form with a `confirmPassword` field that core's
// `resetPasswordSchema` doesn't expose (server doesn't need it; it's a
// client-only confirmation).

import { z } from 'zod';

import {
  forgotPasswordSchema,
  resetPasswordSchema,
  signInSchema,
  signUpSchema,
} from '@barel/core/schemas';

/** Sign-up form: required `name` (form-side override per US-001). */
export const signUpFormSchema = signUpSchema
  .extend({
    name: z.string().trim().min(1).max(120),
    acceptTerms: z.literal(true),
  })
  .strict();
export type SignUpFormInput = z.infer<typeof signUpFormSchema>;

/** Sign-in form — same as core, plus `rememberMe`. */
export const signInFormSchema = signInSchema
  .extend({
    rememberMe: z.boolean().optional(),
  })
  .strict();
export type SignInFormInput = z.infer<typeof signInFormSchema>;

/** Forgot-password form — direct alias of the core schema. */
export const forgotPasswordFormSchema = forgotPasswordSchema;
export type ForgotPasswordFormInput = z.infer<typeof forgotPasswordFormSchema>;

/**
 * Reset-password form — adds `confirmPassword` and a refine that compares
 * against `newPassword`. The token comes from the URL; we keep it on the
 * schema so a single submit object can drive the API call.
 */
const resetPasswordFormBase = resetPasswordSchema
  .extend({
    confirmPassword: z.string().min(1).max(256),
  })
  .strict();

export const resetPasswordFormSchema = resetPasswordFormBase.refine(
  (data: z.infer<typeof resetPasswordFormBase>) => data.confirmPassword === data.newPassword,
  {
    path: ['confirmPassword'],
    message: 'confirmMismatch',
  },
);
export type ResetPasswordFormInput = z.infer<typeof resetPasswordFormSchema>;
